import React, { useState, useEffect } from 'react';
import { Briefcase, DollarSign, Clock, Star, Zap, ArrowRight } from 'lucide-react';

const API = 'https://talentflow-backend-production-09dd.up.railway.app/api';

export default function JobRecommendations({ token }) {
  const [recommendations, setRecommendations] = useState([]);
  const [bench, setBench] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [benchRes, jobsRes] = await Promise.all([
        fetch(`${API}/bench/my`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/requirements/live`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const benchData = await benchRes.json();
      const jobsData = await jobsRes.json();

      const myBench = benchData.resources || [];
      const liveJobs = jobsData.requirements || [];

      setBench(myBench);

      // AI-like matching — skills match karo
      const scored = liveJobs.map(job => {
        const jobSkills = (job.skills || []).map(s => s.toLowerCase());
        let matchCount = 0;
        let matchedCandidates = [];

        myBench.forEach(candidate => {
          const candidateSkills = (candidate.skills || []).map(s => s.toLowerCase());
          const matches = candidateSkills.filter(s => jobSkills.includes(s));
          if (matches.length > 0) {
            matchCount += matches.length;
            matchedCandidates.push({ ...candidate, matchedSkills: matches });
          }
        });

        const matchScore = jobSkills.length > 0 ? Math.round((matchCount / jobSkills.length) * 100) : 0;
        return { ...job, matchScore, matchedCandidates };
      });

      // Sort by match score
      const sorted = scored.sort((a, b) => b.matchScore - a.matchScore);
      setRecommendations(sorted);
    } catch {}
    setLoading(false);
  };

  const getMatchColor = (score) => {
    if (score >= 70) return { color: '#16a34a', bg: '#dcfce7', label: 'Excellent Match' };
    if (score >= 40) return { color: '#ca8a04', bg: '#fef9c3', label: 'Good Match' };
    return { color: '#6366f1', bg: '#ede9fe', label: 'Partial Match' };
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>Finding best matches...</div>;

  const totalBenchSkills = [...new Set(bench.flatMap(b => b.skills || []))];

  return (
    <div>
      {/* Summary */}
      <div style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', borderRadius: '12px', padding: '20px 24px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ color: 'white', fontSize: '16px', fontWeight: '700', margin: '0 0 4px' }}>🎯 AI Job Recommendations</h3>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', margin: 0 }}>
            Based on {bench.length} candidates and {totalBenchSkills.length} skills in your bench
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: 'white', fontSize: '32px', fontWeight: '800' }}>{recommendations.filter(r => r.matchScore > 0).length}</div>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>matching jobs</div>
        </div>
      </div>

      {/* Your bench skills */}
      {totalBenchSkills.length > 0 && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
          <p style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', margin: '0 0 10px' }}>YOUR BENCH SKILLS</p>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {totalBenchSkills.map(s => (
              <span key={s} style={{ padding: '3px 10px', background: '#ede9fe', color: '#6366f1', borderRadius: '20px', fontSize: '12px', fontWeight: '500' }}>{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Job recommendations */}
      <div style={{ display: 'grid', gap: '16px' }}>
        {recommendations.map(job => {
          const mc = getMatchColor(job.matchScore);
          return (
            <div key={job._id} style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ color: '#1e293b', fontSize: '16px', fontWeight: '700', margin: '0 0 6px' }}>{job.title}</h3>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <span style={{ color: '#64748b', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}><Briefcase size={13} /> {job.experience} yrs exp</span>
                    <span style={{ color: '#64748b', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}><DollarSign size={13} /> Rs.{job.adminBudget?.toLocaleString() || job.budget?.toLocaleString()}/month</span>
                    {job.deadline && <span style={{ color: '#64748b', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}><Clock size={13} /> {Math.max(0, Math.floor((new Date(job.deadline) - new Date()) / 60000))}m left</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'center', marginLeft: '16px', flexShrink: 0 }}>
                  <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: mc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    <div style={{ color: mc.color, fontSize: '16px', fontWeight: '800', lineHeight: 1 }}>{job.matchScore}%</div>
                  </div>
                  <div style={{ color: mc.color, fontSize: '10px', fontWeight: '600', marginTop: '4px' }}>{mc.label}</div>
                </div>
              </div>

              {/* Required skills */}
              <div style={{ marginBottom: '12px' }}>
                <p style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '600', margin: '0 0 6px' }}>REQUIRED SKILLS</p>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {(job.skills || []).map(s => {
                    const mySkills = totalBenchSkills.map(sk => sk.toLowerCase());
                    const isMatch = mySkills.includes(s.toLowerCase());
                    return (
                      <span key={s} style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
                        background: isMatch ? '#dcfce7' : '#f1f5f9',
                        color: isMatch ? '#16a34a' : '#64748b',
                        border: isMatch ? '1px solid #86efac' : '1px solid #e2e8f0'
                      }}>
                        {isMatch ? '✓ ' : ''}{s}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Matching candidates */}
              {job.matchedCandidates.length > 0 && (
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px', marginBottom: '14px', border: '1px solid #e2e8f0' }}>
                  <p style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', margin: '0 0 8px' }}>YOUR MATCHING CANDIDATES</p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {job.matchedCandidates.slice(0, 3).map(c => (
                      <div key={c._id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', background: 'white', borderRadius: '20px', border: '1px solid #e2e8f0' }}>
                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '10px', fontWeight: '700' }}>
                          {c.name?.charAt(0)}
                        </div>
                        <span style={{ color: '#1e293b', fontSize: '12px', fontWeight: '600' }}>{c.isAnonymous ? 'Anonymous' : c.name}</span>
                        <span style={{ color: '#16a34a', fontSize: '11px' }}>({c.matchedSkills.length} skills)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', background: job.matchScore >= 40 ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : '#f1f5f9', color: job.matchScore >= 40 ? 'white' : '#64748b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit' }}>
                <Zap size={14} /> Submit Candidate <ArrowRight size={13} />
              </button>
            </div>
          );
        })}
      </div>

      {recommendations.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px', background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', color: '#94a3b8' }}>
          <Star size={40} color="#e2e8f0" style={{ margin: '0 auto 12px', display: 'block' }} />
          <p style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: '500' }}>No live jobs available right now</p>
          <p style={{ margin: 0, fontSize: '13px' }}>Check back later for new opportunities</p>
        </div>
      )}
    </div>
  );
}
